import { useMemo, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import {
  Activity,
  AlertTriangle,
  Ban,
  CheckCircle2,
  ClipboardList,
  Coins,
  Database,
  KeyRound,
  Pencil,
  PlusCircle,
  RefreshCcw,
  RotateCcw,
  SlidersHorizontal,
  Store,
  X,
} from 'lucide-react';
import { useAuth } from '../../AuthContext';
import {
  OC_REQUEST_STATUS_LABEL,
  OC_REQUEST_STATUS_TONE,
  OC_REQUEST_TYPE_LABEL,
  OC_TRANSACTION_TYPE_OPTIONS,
  describeOcRequestTarget,
  ocTransactionTypeLabel,
} from '../../lib/oc';
import {
  LOW_OC_THRESHOLD,
  USER_PASSWORD_RESET_CONFIRMATION,
  assertConfirmation,
  canRequestUserPasswordReset,
  formatCurrency,
  formatDateTime,
  formatOc,
  getUserAccountId,
  maskPhone,
  shortId,
  stringifySystemSettingValue,
  validateCreateAdminUserInput,
  validateUserPasswordResetInput,
  type AuditFilters,
  type AuditLogRow,
  type CreateAdminUserInput,
  type FranchiseFilters,
  type FranchiseRow,
  type OcFilters,
  type OcRequestRow,
  type OcRequestStatusFilter,
  type OcTransactionRow,
  type PaginatedResult,
  type SettingFilters,
  type StoreFilters,
  type StoreRow,
  type SystemSettingRow,
  type UserFilters,
  type UserRow,
} from './data';
import {
  useAdjustOcCreditMutation,
  useActiveFranchiseOptions,
  useApproveOcRequestMutation,
  useAuditLogPage,
  useCreateAdminUserMutation,
  useCreateFranchiseMutation,
  useCreateStoreMutation,
  useCursorPagination,
  useFranchisePage,
  useIssueOcCreditMutation,
  useOcRequestPage,
  useOcTransactionPage,
  useRejectOcRequestMutation,
  useResetUserPasswordMutation,
  useSettingPage,
  useSetFranchiseStatusMutation,
  useStoreOptions,
  useStorePage,
  useSuspendUserMutation,
  useSystemDashboardReadModel,
  useUnsuspendUserMutation,
  useUpdateFranchiseMutation,
  useUpdateSystemSettingMutation,
  useUpdateStoreMutation,
  useUserPage,
} from './hooks';

interface DataColumn<T> {
  header: string;
  render: (row: T) => ReactNode;
  align?: 'right';
}

interface DataTableProps<T extends { id: string }> {
  columns: DataColumn<T>[];
  emptyLabel: string;
  rows: T[];
}

interface QuerySectionProps<T extends { id: string }> {
  columns: DataColumn<T>[];
  emptyLabel: string;
  errorLabel: string;
  hasMore?: boolean;
  isFetching: boolean;
  isLoading: boolean;
  onNext?: () => void;
  onRefresh: () => void;
  result?: PaginatedResult<T>;
}

function DataTable<T extends { id: string }>({ columns, emptyLabel, rows }: DataTableProps<T>) {
  if (rows.length === 0) {
    return <div className="empty-state">{emptyLabel}</div>;
  }

  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.header} className={column.align === 'right' ? 'is-right' : undefined}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {columns.map((column) => (
                <td key={column.header} className={column.align === 'right' ? 'is-right' : undefined}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QuerySection<T extends { id: string }>({
  columns,
  emptyLabel,
  errorLabel,
  hasMore,
  isFetching,
  isLoading,
  onNext,
  onRefresh,
  result,
}: QuerySectionProps<T>) {
  if (isLoading) {
    return (
      <section className="data-panel">
        <div className="loading-block">데이터를 불러오는 중입니다.</div>
      </section>
    );
  }

  if (!result) {
    return (
      <section className="data-panel">
        <div className="error-block">
          <strong>{errorLabel}</strong>
          <button className="secondary-button" type="button" onClick={onRefresh}>
            <RefreshCcw size={16} />
            다시 시도
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="data-panel">
      <DataTable columns={columns} emptyLabel={emptyLabel} rows={result.rows} />
      <div className="table-footer">
        <span>{isFetching ? '새로고침 중' : `${result.rows.length}개 표시 중`}</span>
        {hasMore && onNext ? (
          <button className="secondary-button" type="button" onClick={onNext}>
            다음 50개
          </button>
        ) : null}
      </div>
    </section>
  );
}

function FilterInput({
  label,
  onChange,
  placeholder,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  placeholder: string;
  value: string;
}) {
  return (
    <label className="filter-field">
      <span>{label}</span>
      <input type="search" value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function FilterSelect({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  options: Array<{ label: string; value: string }>;
  value: string;
}) {
  return (
    <label className="filter-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ToggleField({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="toggle-field">
      <input type="checkbox" checked={checked} onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function FormField({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  return (
    <label className="form-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function FormHelp({ children }: { children: ReactNode }) {
  return <p className="form-help">{children}</p>;
}

function ActionNotice({
  children,
  tone = 'success',
}: {
  children: ReactNode;
  tone?: 'success' | 'danger';
}) {
  return <div className={`action-notice action-notice--${tone}`}>{children}</div>;
}

function getUserDisplayLabel(user: UserRow): string {
  const accountId = getUserAccountId(user);

  return user.nickname ? `${user.nickname} (${accountId})` : accountId;
}

function ModalFrame({
  children,
  description,
  onClose,
  title,
}: {
  children: ReactNode;
  description: string;
  onClose: () => void;
  title: string;
}) {
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal-panel" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="modal-heading">
          <div>
            <h3 id="modal-title">{title}</h3>
            <p>{description}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose}>
            <X size={18} />
            <span className="sr-only">닫기</span>
          </button>
        </div>
        {children}
      </section>
    </div>
  );
}

function StatusBadge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'success' | 'danger' | 'warning' }) {
  return <span className={`row-badge row-badge--${tone}`}>{children}</span>;
}

function PageHeading({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <section className="page-heading">
      <div>
        <p className="section-eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
    </section>
  );
}

function FranchiseFormDialog({
  franchise,
  mode,
  onClose,
  onDone,
}: {
  franchise?: FranchiseRow;
  mode: 'create' | 'edit';
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const createMutation = useCreateFranchiseMutation();
  const updateMutation = useUpdateFranchiseMutation();
  const [commissionRate, setCommissionRate] = useState(() => String(franchise?.commission_rate ?? 10));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [exchangeRate, setExchangeRate] = useState(() => String(franchise?.exchange_rate ?? 25));
  const [name, setName] = useState(() => franchise?.name ?? '');
  const [ocBalance, setOcBalance] = useState(() => String(franchise?.oc_balance ?? 10000));
  const [region, setRegion] = useState(() => franchise?.region ?? '');
  const isCreate = mode === 'create';
  const isPending = createMutation.isPending || updateMutation.isPending;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    try {
      if (isCreate) {
        await createMutation.mutateAsync({
          commissionRate: Number(commissionRate),
          exchangeRate: Number(exchangeRate),
          name,
          ocBalance: Number(ocBalance),
          region,
        });
        onDone('프랜차이즈를 생성했습니다.');
      } else if (franchise) {
        await updateMutation.mutateAsync({
          beforeValue: franchise,
          commissionRate: Number(commissionRate),
          exchangeRate: Number(exchangeRate),
          id: franchise.id,
          name,
          region,
        });
        onDone('프랜차이즈를 수정했습니다.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '프랜차이즈 작업에 실패했습니다.');
    }
  }

  return (
    <ModalFrame
      title={isCreate ? '프랜차이즈 추가' : '프랜차이즈 수정'}
      description={isCreate ? '새 프랜차이즈의 기본 운영 값을 등록합니다.' : 'OC 잔액을 제외한 기본 운영 값을 수정합니다.'}
      onClose={onClose}
    >
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <FormField label="프랜차이즈명">
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="예: 강남 프랜차이즈" />
        </FormField>
        <FormField label="지역">
          <input value={region} onChange={(event) => setRegion(event.target.value)} placeholder="예: 서울" />
        </FormField>
        {isCreate ? (
          <FormField label="초기 OC">
            <input value={ocBalance} onChange={(event) => setOcBalance(event.target.value)} inputMode="numeric" placeholder="10000" />
          </FormField>
        ) : (
          <FormHelp>현재 OC 잔액: {formatOc(franchise?.oc_balance)}</FormHelp>
        )}
        <FormField label="OC 환율 (1 OC당 원)">
          <input value={exchangeRate} onChange={(event) => setExchangeRate(event.target.value)} inputMode="decimal" placeholder="25" />
        </FormField>
        <FormField label="수수료율">
          <input value={commissionRate} onChange={(event) => setCommissionRate(event.target.value)} inputMode="decimal" placeholder="10" />
        </FormField>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            취소
          </button>
          <button className="primary-button" type="submit" disabled={isPending}>
            {isPending ? '저장 중' : isCreate ? '생성' : '저장'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

function FranchiseStatusDialog({
  franchise,
  onClose,
  onDone,
  status,
}: {
  franchise: FranchiseRow;
  onClose: () => void;
  onDone: (message: string) => void;
  status: 'active' | 'inactive';
}) {
  const statusMutation = useSetFranchiseStatusMutation();
  const [confirmationText, setConfirmationText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const isDeactivation = status === 'inactive';
  const confirmationPhrase = isDeactivation ? 'DEACTIVATE FRANCHISE' : 'ACTIVATE FRANCHISE';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    try {
      await statusMutation.mutateAsync({
        beforeValue: franchise,
        confirmationText,
        id: franchise.id,
        reason,
        status,
      });
      onDone(isDeactivation ? '프랜차이즈를 비활성화했습니다.' : '프랜차이즈를 활성화했습니다.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '프랜차이즈 상태 변경에 실패했습니다.');
    }
  }

  return (
    <ModalFrame
      title={isDeactivation ? '프랜차이즈 비활성화' : '프랜차이즈 활성화'}
      description={`${franchise.name} 상태를 변경합니다.`}
      onClose={onClose}
    >
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <FormField label="사유">
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="상태 변경 사유" rows={3} />
        </FormField>
        <FormField label="확인 문구">
          <input value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} placeholder={confirmationPhrase} />
        </FormField>
        <FormHelp>
          확인 문구 <strong>{confirmationPhrase}</strong>를 정확히 입력해야 실행됩니다.
        </FormHelp>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            취소
          </button>
          <button className={isDeactivation ? 'danger-button' : 'primary-button'} type="submit" disabled={statusMutation.isPending}>
            {statusMutation.isPending ? '처리 중' : isDeactivation ? '비활성화' : '활성화'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

function StoreFormDialog({
  franchises,
  mode,
  onClose,
  onDone,
  store,
}: {
  franchises: FranchiseRow[];
  mode: 'create' | 'edit';
  onClose: () => void;
  onDone: (message: string) => void;
  store?: StoreRow;
}) {
  const createMutation = useCreateStoreMutation();
  const updateMutation = useUpdateStoreMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pointRate, setPointRate] = useState(() => String(store?.krw_per_1000_points ?? 1000));
  const [franchiseId, setFranchiseId] = useState(() => store?.franchise_id ?? '');
  const [name, setName] = useState(() => store?.name ?? '');
  const [ocBalance, setOcBalance] = useState(() => String(store?.oc_balance ?? 0));
  const isCreate = mode === 'create';
  const isPending = createMutation.isPending || updateMutation.isPending;
  const franchiseOptions = useMemo(() => {
    if (!store?.franchise_id || franchises.some((franchise) => franchise.id === store.franchise_id) || !store.franchise) {
      return franchises;
    }

    return [
      ...franchises,
      {
        commission_rate: null,
        created_at: store.created_at,
        exchange_rate: null,
        id: store.franchise.id,
        name: store.franchise.name,
        oc_balance: 0,
        region: null,
        status: 'inactive',
        updated_at: store.updated_at,
      },
    ];
  }, [franchises, store]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    try {
      if (isCreate) {
        await createMutation.mutateAsync({
          krwPer1000Points: Number(pointRate),
          franchiseId,
          name,
          ocBalance: Number(ocBalance),
        });
        onDone('매장을 생성했습니다.');
      } else if (store) {
        await updateMutation.mutateAsync({
          beforeValue: store,
          krwPer1000Points: Number(pointRate),
          franchiseId,
          id: store.id,
          name,
        });
        onDone('매장을 수정했습니다.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '매장 작업에 실패했습니다.');
    }
  }

  return (
    <ModalFrame
      title={isCreate ? '매장 추가' : '매장 수정'}
      description={isCreate ? '새 매장을 등록합니다.' : 'OC 잔액을 제외한 매장 기본 정보를 수정합니다.'}
      onClose={onClose}
    >
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <FormField label="매장명">
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="예: 강남점" />
        </FormField>
        <FormField label="프랜차이즈">
          <select value={franchiseId} onChange={(event) => setFranchiseId(event.target.value)}>
            <option value="">프랜차이즈 없음</option>
            {franchiseOptions.map((franchise) => (
              <option key={franchise.id} value={franchise.id}>
                {franchise.name}
              </option>
            ))}
          </select>
        </FormField>
        {isCreate ? (
          <FormField label="초기 OC">
            <input value={ocBalance} onChange={(event) => setOcBalance(event.target.value)} inputMode="numeric" placeholder="0" />
          </FormField>
        ) : (
          <FormHelp>현재 OC 잔액: {formatOc(store?.oc_balance)}</FormHelp>
        )}
        <FormField label="포인트 환율 (1,000포인트당 원)">
          <input value={pointRate} onChange={(event) => setPointRate(event.target.value)} inputMode="numeric" placeholder="1000" />
        </FormField>
        <FormHelp>1000이면 1포인트 = 1원, 500이면 1원 = 2포인트입니다. 바이인 부족분 현금과 아이템 수수료 계산에 씁니다.</FormHelp>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            취소
          </button>
          <button className="primary-button" type="submit" disabled={isPending}>
            {isPending ? '저장 중' : isCreate ? '생성' : '저장'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

function UserStatusDialog({
  currentAdminId,
  mode,
  onClose,
  onDone,
  user,
}: {
  currentAdminId: string;
  mode: 'suspend' | 'unsuspend';
  onClose: () => void;
  onDone: (message: string) => void;
  user: UserRow;
}) {
  const suspendMutation = useSuspendUserMutation();
  const unsuspendMutation = useUnsuspendUserMutation();
  const [confirmationText, setConfirmationText] = useState('');
  const [duration, setDuration] = useState('indefinite');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const isSuspending = mode === 'suspend';
  const confirmationPhrase = isSuspending ? 'SUSPEND USER' : 'UNSUSPEND USER';
  const isPending = suspendMutation.isPending || unsuspendMutation.isPending;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    try {
      assertConfirmation(confirmationText, confirmationPhrase);

      if (isSuspending) {
        await suspendMutation.mutateAsync({
          currentAdminId,
          duration,
          reason,
          userId: user.id,
        });
        onDone('사용자를 정지했습니다.');
      } else {
        await unsuspendMutation.mutateAsync({
          currentAdminId,
          reason,
          userId: user.id,
        });
        onDone('사용자 정지를 해제했습니다.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '작업에 실패했습니다.');
    }
  }

  return (
    <ModalFrame
      title={isSuspending ? '사용자 정지' : '사용자 정지 해제'}
      description={`${getUserDisplayLabel(user)} 계정 상태를 변경합니다.`}
      onClose={onClose}
    >
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <FormField label="사유">
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder={isSuspending ? '정지 사유' : '해제 사유'} rows={3} />
        </FormField>
        {isSuspending ? (
          <FormField label="기간">
            <select value={duration} onChange={(event) => setDuration(event.target.value)}>
              <option value="indefinite">무기한</option>
              <option value="1d">1일</option>
              <option value="7d">7일</option>
              <option value="30d">30일</option>
            </select>
          </FormField>
        ) : null}
        <FormField label="확인 문구">
          <input value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} placeholder={confirmationPhrase} />
        </FormField>
        <FormHelp>
          확인 문구 <strong>{confirmationPhrase}</strong>를 정확히 입력해야 실행됩니다.
        </FormHelp>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            취소
          </button>
          <button className={isSuspending ? 'danger-button' : 'primary-button'} type="submit" disabled={isPending}>
            {isPending ? '처리 중' : isSuspending ? '정지' : '정지 해제'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

function UserPasswordResetDialog({
  currentAdminId,
  onClose,
  onDone,
  user,
}: {
  currentAdminId: string;
  onClose: () => void;
  onDone: (message: string) => void;
  user: UserRow;
}) {
  const resetMutation = useResetUserPasswordMutation();
  const [confirmationText, setConfirmationText] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [reason, setReason] = useState('');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    try {
      const validationError = validateUserPasswordResetInput({
        confirmPassword,
        confirmationText,
        currentAdminId,
        newPassword,
        reason,
        userId: user.id,
      });

      if (validationError) {
        throw new Error(validationError);
      }

      await resetMutation.mutateAsync({
        confirmPassword,
        confirmationText,
        currentAdminId,
        newPassword,
        reason,
        userId: user.id,
      });
      onDone('사용자 비밀번호를 재설정했습니다.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '비밀번호 재설정에 실패했습니다.');
    }
  }

  return (
    <ModalFrame
      title="비밀번호 재설정"
      description={`${getUserDisplayLabel(user)} 계정의 임시 비밀번호를 설정합니다.`}
      onClose={onClose}
    >
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <FormField label="임시 비밀번호">
          <input
            value={newPassword}
            type="password"
            autoComplete="new-password"
            onChange={(event) => setNewPassword(event.target.value)}
            disabled={resetMutation.isPending}
          />
        </FormField>
        <FormField label="임시 비밀번호 확인">
          <input
            value={confirmPassword}
            type="password"
            autoComplete="new-password"
            onChange={(event) => setConfirmPassword(event.target.value)}
            disabled={resetMutation.isPending}
          />
        </FormField>
        <FormField label="사유">
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="재설정 사유" rows={3} disabled={resetMutation.isPending} />
        </FormField>
        <FormField label="확인 문구">
          <input
            value={confirmationText}
            onChange={(event) => setConfirmationText(event.target.value)}
            placeholder={USER_PASSWORD_RESET_CONFIRMATION}
            disabled={resetMutation.isPending}
          />
        </FormField>
        <FormHelp>
          확인 문구 <strong>{USER_PASSWORD_RESET_CONFIRMATION}</strong>를 정확히 입력해야 실행됩니다. 비밀번호 값은 감사 로그에 저장하지 않습니다.
        </FormHelp>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose} disabled={resetMutation.isPending}>
            취소
          </button>
          <button className="danger-button" type="submit" disabled={resetMutation.isPending || !newPassword || !confirmPassword || !reason || !confirmationText}>
            {resetMutation.isPending ? '재설정 중' : '재설정'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

function CreateAdminUserDialog({
  franchises,
  onClose,
  onDone,
  stores,
}: {
  franchises: FranchiseRow[];
  onClose: () => void;
  onDone: (message: string) => void;
  stores: StoreRow[];
}) {
  const createMutation = useCreateAdminUserMutation();
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [franchiseId, setFranchiseId] = useState('');
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [role, setRole] = useState<CreateAdminUserInput['role']>('super_admin');
  const [staffPhoneNumber, setStaffPhoneNumber] = useState('');
  const [storeId, setStoreId] = useState('');
  const [username, setUsername] = useState('');
  const selectedStore = stores.find((store) => store.id === storeId);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    const input: CreateAdminUserInput = {
      confirmPassword,
      franchiseId: role === 'super_admin' ? franchiseId : selectedStore?.franchise_id ?? '',
      nickname,
      password,
      reason,
      role,
      source: 'system_admin_react',
      staffPhoneNumber,
      storeId: role === 'staff_admin' ? storeId : '',
      username: username.trim(),
    };

    const validationError = validateCreateAdminUserInput(input);

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      await createMutation.mutateAsync(input);
      onDone('관리자 계정을 생성했습니다.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '관리자 계정을 생성하지 못했습니다.');
    }
  }

  return (
    <ModalFrame title="관리자 추가" description="프랜차이즈 관리자 또는 매장 관리자 계정을 생성합니다." onClose={onClose}>
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <FormField label="역할">
          <select
            value={role}
            onChange={(event) => {
              setRole(event.target.value as CreateAdminUserInput['role']);
              setFranchiseId('');
              setStoreId('');
            }}
            disabled={createMutation.isPending}
          >
            <option value="super_admin">프랜차이즈 관리자</option>
            <option value="staff_admin">매장 관리자</option>
          </select>
        </FormField>
        <FormField label="계정 ID">
          <input
            value={username}
            autoComplete="username"
            placeholder="예: franchise_admin_01"
            onChange={(event) => setUsername(event.target.value)}
            disabled={createMutation.isPending}
          />
        </FormField>
        <FormField label="이름 또는 표시명">
          <input value={nickname} onChange={(event) => setNickname(event.target.value)} disabled={createMutation.isPending} />
        </FormField>
        {role === 'super_admin' ? (
          <FormField label="프랜차이즈">
            <select value={franchiseId} onChange={(event) => setFranchiseId(event.target.value)} disabled={createMutation.isPending}>
              <option value="">선택</option>
              {franchises.map((franchise) => (
                <option key={franchise.id} value={franchise.id}>
                  {franchise.name}
                </option>
              ))}
            </select>
          </FormField>
        ) : (
          <FormField label="매장">
            <select value={storeId} onChange={(event) => setStoreId(event.target.value)} disabled={createMutation.isPending}>
              <option value="">선택</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.franchise?.name ? `${store.franchise.name} / ` : ''}
                  {store.name}
                </option>
              ))}
            </select>
          </FormField>
        )}
        <FormField label="연락처">
          <input value={staffPhoneNumber} inputMode="tel" onChange={(event) => setStaffPhoneNumber(event.target.value)} disabled={createMutation.isPending} />
        </FormField>
        <FormField label="임시 비밀번호">
          <input
            value={password}
            type="password"
            autoComplete="new-password"
            onChange={(event) => setPassword(event.target.value)}
            disabled={createMutation.isPending}
          />
        </FormField>
        <FormField label="임시 비밀번호 확인">
          <input
            value={confirmPassword}
            type="password"
            autoComplete="new-password"
            onChange={(event) => setConfirmPassword(event.target.value)}
            disabled={createMutation.isPending}
          />
        </FormField>
        <FormField label="생성 사유">
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="계정 생성 사유" rows={3} disabled={createMutation.isPending} />
        </FormField>
        <FormHelp>계정 ID는 로그인 시 내부 이메일로 정규화되며 비밀번호 값은 감사 로그에 저장하지 않습니다.</FormHelp>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose} disabled={createMutation.isPending}>
            취소
          </button>
          <button className="primary-button" type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? '생성 중' : '생성'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

function OcActionDialog({
  franchises,
  mode,
  onClose,
  onDone,
}: {
  franchises: FranchiseRow[];
  mode: 'issue' | 'adjust';
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const issueMutation = useIssueOcCreditMutation();
  const adjustMutation = useAdjustOcCreditMutation();
  const [amount, setAmount] = useState('');
  const [amountKrw, setAmountKrw] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [franchiseId, setFranchiseId] = useState('');
  const [reason, setReason] = useState('');
  const isIssue = mode === 'issue';
  const confirmationPhrase = isIssue ? 'ISSUE OC' : 'ADJUST OC';
  const isPending = issueMutation.isPending || adjustMutation.isPending;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    try {
      assertConfirmation(confirmationText, confirmationPhrase);

      if (!franchiseId) {
        throw new Error('프랜차이즈를 선택하세요.');
      }

      const parsedAmount = Number(amount);

      if (isIssue) {
        await issueMutation.mutateAsync({
          amount: parsedAmount,
          amountKrw: Number(amountKrw),
          franchiseId,
          reason,
        });
        onDone('OC를 발행했습니다.');
      } else {
        await adjustMutation.mutateAsync({
          amount: parsedAmount,
          franchiseId,
          reason,
        });
        onDone('OC를 조정했습니다.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'OC 작업에 실패했습니다.');
    }
  }

  return (
    <ModalFrame
      title={isIssue ? 'OC 발행' : 'OC 조정'}
      description={isIssue ? '프랜차이즈 잔액을 증가시키고 결제 금액을 기록합니다.' : '프랜차이즈 잔액을 수동 조정합니다. 음수 입력은 차감입니다.'}
      onClose={onClose}
    >
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <FormField label="프랜차이즈">
          <select value={franchiseId} onChange={(event) => setFranchiseId(event.target.value)}>
            <option value="">선택</option>
            {franchises.map((franchise) => (
              <option key={franchise.id} value={franchise.id}>
                {franchise.name} / {formatOc(franchise.oc_balance)}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label={isIssue ? '발행 OC' : '조정 OC'}>
          <input value={amount} onChange={(event) => setAmount(event.target.value)} inputMode="numeric" placeholder={isIssue ? '1000' : '-100'} />
        </FormField>
        {isIssue ? (
          <FormField label="결제 금액">
            <input value={amountKrw} onChange={(event) => setAmountKrw(event.target.value)} inputMode="decimal" placeholder="25000" />
          </FormField>
        ) : null}
        <FormField label="사유">
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="작업 사유" rows={3} />
        </FormField>
        <FormField label="확인 문구">
          <input value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} placeholder={confirmationPhrase} />
        </FormField>
        <FormHelp>
          확인 문구 <strong>{confirmationPhrase}</strong>를 정확히 입력해야 실행됩니다.
        </FormHelp>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            취소
          </button>
          <button className="primary-button" type="submit" disabled={isPending}>
            {isPending ? '처리 중' : isIssue ? '발행' : '조정'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

function OcRequestReviewDialog({
  mode,
  onClose,
  onDone,
  request,
}: {
  mode: 'approve' | 'reject';
  onClose: () => void;
  onDone: (message: string) => void;
  request: OcRequestRow;
}) {
  const approveMutation = useApproveOcRequestMutation();
  const rejectMutation = useRejectOcRequestMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reviewReason, setReviewReason] = useState('');
  const isApprove = mode === 'approve';
  const isPending = approveMutation.isPending || rejectMutation.isPending;
  const target = describeOcRequestTarget(request);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isPending) return;
    setErrorMessage(null);

    if (!isApprove && reviewReason.trim().length < 3) {
      setErrorMessage('반려 사유는 3자 이상 입력하세요.');
      return;
    }

    try {
      if (isApprove) {
        const result = await approveMutation.mutateAsync({ id: request.id, reviewReason });
        onDone(`${target.label}에 ${formatOc(request.amount)}를 반영했습니다. 변경 후 잔액은 ${formatOc(result.new_balance)}입니다.`);
      } else {
        await rejectMutation.mutateAsync({ id: request.id, reviewReason });
        onDone('OC 요청을 반려했습니다.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'OC 요청을 처리하지 못했습니다.');
    }
  }

  return (
    <ModalFrame
      title={isApprove ? 'OC 요청 승인' : 'OC 요청 반려'}
      description={
        isApprove
          ? `승인하면 아래 대상의 OC 잔액에 즉시 반영되며 되돌릴 수 없습니다.`
          : '반려 사유는 요청한 프랜차이즈 관리자에게 표시됩니다.'
      }
      onClose={isPending ? () => undefined : onClose}
    >
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <dl className="summary-list">
          <dt>반영 대상</dt>
          <dd>
            {target.label} ({target.kind})
          </dd>
          <dt>요청 유형</dt>
          <dd>{OC_REQUEST_TYPE_LABEL[request.request_type]}</dd>
          <dt>OC 수량</dt>
          <dd>{formatOc(request.amount)}</dd>
          <dt>결제 금액</dt>
          <dd>{request.amount_krw == null ? '-' : formatCurrency(request.amount_krw)}</dd>
          <dt>요청자</dt>
          <dd>{request.requested_by_name ?? shortId(request.requested_by)}</dd>
          <dt>요청 사유</dt>
          <dd>{request.reason}</dd>
        </dl>
        <FormField label={isApprove ? '승인 메모 (선택)' : '반려 사유 (3자 이상)'}>
          <textarea
            value={reviewReason}
            onChange={(event) => setReviewReason(event.target.value)}
            rows={3}
            maxLength={200}
            disabled={isPending}
          />
        </FormField>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose} disabled={isPending}>
            취소
          </button>
          <button className={isApprove ? 'primary-button' : 'danger-button'} type="submit" disabled={isPending}>
            {isPending ? '처리 중' : isApprove ? `${formatOc(request.amount)} 승인` : '반려'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

function SettingUpdateDialog({
  adminId,
  onClose,
  onDone,
  setting,
}: {
  adminId: string;
  onClose: () => void;
  onDone: (message: string) => void;
  setting: SystemSettingRow;
}) {
  const updateMutation = useUpdateSystemSettingMutation();
  const [confirmationText, setConfirmationText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [valueText, setValueText] = useState(() => stringifySystemSettingValue(setting.value, setting.value_type));
  const confirmationPhrase = `UPDATE ${setting.key}`;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    try {
      await updateMutation.mutateAsync({
        adminId,
        beforeValue: setting.value,
        confirmationText,
        id: setting.id,
        key: setting.key,
        reason,
        valueText,
        valueType: setting.value_type,
      });
      onDone('시스템 설정을 업데이트했습니다.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '시스템 설정 업데이트에 실패했습니다.');
    }
  }

  return (
    <ModalFrame title="시스템 설정 업데이트" description={`${setting.key} 값을 변경합니다.`} onClose={onClose}>
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <FormField label={`값 (${setting.value_type})`}>
          <textarea value={valueText} onChange={(event) => setValueText(event.target.value)} rows={setting.value_type === 'json' ? 7 : 3} />
        </FormField>
        <FormField label="사유">
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="변경 사유" rows={3} />
        </FormField>
        <FormField label="확인 문구">
          <input value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} placeholder={confirmationPhrase} />
        </FormField>
        <FormHelp>
          확인 문구 <strong>{confirmationPhrase}</strong>를 정확히 입력해야 실행됩니다.
        </FormHelp>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            취소
          </button>
          <button className="primary-button" type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '저장 중' : '저장'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

export function SystemDashboardContent() {
  const { data, error, isFetching, isLoading, refetch } = useSystemDashboardReadModel();

  if (isLoading) {
    return (
      <section className="data-panel">
        <div className="loading-block">플랫폼 현황을 불러오는 중입니다.</div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="data-panel">
        <div className="error-block">
          <strong>플랫폼 현황을 불러오지 못했습니다.</strong>
          <button className="secondary-button" type="button" onClick={() => void refetch()}>
            <RefreshCcw size={16} />
            다시 시도
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="stat-grid" aria-label="플랫폼 지표">
        {data.metrics.map((metric) => (
          <div className="stat-card" key={metric.label}>
            <Database size={22} />
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
            <small>{metric.note}</small>
          </div>
        ))}
      </section>

      <section className="dashboard-grid">
        <div className="data-panel">
          <div className="panel-heading">
            <span>
              <AlertTriangle size={18} />
              낮은 OC 프랜차이즈
            </span>
            <small>{LOW_OC_THRESHOLD} OC 미만</small>
          </div>
          <DataTable
            columns={[
              { header: '프랜차이즈', render: (row: FranchiseRow) => row.name },
              { header: '지역', render: (row) => row.region ?? '-' },
              { header: 'OC', align: 'right', render: (row) => formatOc(row.oc_balance) },
            ]}
            emptyLabel="낮은 OC 프랜차이즈가 없습니다."
            rows={data.lowBalanceFranchises}
          />
        </div>

        <div className="data-panel">
          <div className="panel-heading">
            <span>
              <Coins size={18} />
              최근 OC 거래
            </span>
            <small>{isFetching ? '갱신 중' : '최신 5건'}</small>
          </div>
          <DataTable
            columns={[
              { header: '일시', render: (row: OcTransactionRow) => formatDateTime(row.created_at) },
              { header: '유형', render: (row) => ocTransactionTypeLabel(row.transaction_type) },
              { header: 'OC', align: 'right', render: (row) => formatOc(row.amount_oc ?? row.amount) },
            ]}
            emptyLabel="최근 OC 거래가 없습니다."
            rows={data.recentOcTransactions}
          />
        </div>

        <div className="data-panel dashboard-grid__wide">
          <div className="panel-heading">
            <span>
              <ClipboardList size={18} />
              최근 감사 로그
            </span>
            <small>최신 5건</small>
          </div>
          <DataTable
            columns={[
              { header: '일시', render: (row: AuditLogRow) => formatDateTime(row.created_at) },
              { header: '작업', render: (row) => row.action_type },
              { header: '대상', render: (row) => row.target_type },
              { header: '대상 ID', render: (row) => shortId(row.target_id) },
            ]}
            emptyLabel="최근 감사 로그가 없습니다."
            rows={data.recentAuditLogs}
          />
        </div>
      </section>
    </>
  );
}

export function SystemFranchisesPage() {
  const pagination = useCursorPagination();
  const [notice, setNotice] = useState<string | null>(null);
  const [filters, setFilters] = useState<FranchiseFilters>({});
  const [franchiseForm, setFranchiseForm] = useState<{ mode: 'create' | 'edit'; franchise?: FranchiseRow } | null>(null);
  const [statusDialog, setStatusDialog] = useState<{ franchise: FranchiseRow; status: 'active' | 'inactive' } | null>(null);
  const query = useFranchisePage(filters, pagination.cursor);

  const columns = useMemo<DataColumn<FranchiseRow>[]>(
    () => [
      { header: '프랜차이즈', render: (row) => row.name },
      { header: '상태', render: (row) => <StatusBadge tone={row.status === 'active' ? 'success' : 'danger'}>{row.status}</StatusBadge> },
      { header: '지역', render: (row) => row.region ?? '-' },
      { header: 'OC', align: 'right', render: (row) => formatOc(row.oc_balance) },
      { header: 'OC 환율', align: 'right', render: (row) => `${formatCurrency(row.exchange_rate)} / OC` },
      { header: '생성일', render: (row) => formatDateTime(row.created_at) },
      {
        header: '작업',
        render: (row) => (
          <div className="row-actions">
            <button className="secondary-button compact-button" type="button" onClick={() => setFranchiseForm({ mode: 'edit', franchise: row })}>
              <Pencil size={15} />
              수정
            </button>
            {row.status === 'inactive' ? (
              <button className="secondary-button compact-button" type="button" onClick={() => setStatusDialog({ franchise: row, status: 'active' })}>
                <RotateCcw size={15} />
                활성화
              </button>
            ) : (
              <button className="danger-outline-button compact-button" type="button" onClick={() => setStatusDialog({ franchise: row, status: 'inactive' })}>
                <Ban size={15} />
                비활성화
              </button>
            )}
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="page-stack">
      <PageHeading eyebrow="System Admin" title="프랜차이즈" description="플랫폼 전체 프랜차이즈의 상태와 OC 잔액을 확인하고 기본 운영 값을 관리합니다." />
      {notice ? <ActionNotice>{notice}</ActionNotice> : null}
      <section className="action-toolbar">
        <button className="primary-button toolbar-button" type="button" onClick={() => setFranchiseForm({ mode: 'create' })}>
          <PlusCircle size={17} />
          프랜차이즈 추가
        </button>
      </section>
      <section className="filter-bar">
        <FilterInput
          label="검색"
          placeholder="프랜차이즈명"
          value={filters.search ?? ''}
          onChange={(search) => {
            pagination.reset();
            setFilters((current) => ({ ...current, search }));
          }}
        />
        <FilterSelect
          label="상태"
          value={filters.status ?? ''}
          options={[
            { label: '전체', value: '' },
            { label: 'active', value: 'active' },
            { label: 'inactive', value: 'inactive' },
          ]}
          onChange={(status) => {
            pagination.reset();
            setFilters((current) => ({ ...current, status: status || undefined }));
          }}
        />
      </section>
      <QuerySection
        columns={columns}
        emptyLabel="조건에 맞는 프랜차이즈가 없습니다."
        errorLabel="프랜차이즈 목록을 불러오지 못했습니다."
        hasMore={query.data?.hasMore}
        isFetching={query.isFetching}
        isLoading={query.isLoading}
        onNext={() => pagination.goNext(query.data?.nextCursor ?? null)}
        onRefresh={() => void query.refetch()}
        result={query.data}
      />
      {franchiseForm ? (
        <FranchiseFormDialog
          mode={franchiseForm.mode}
          franchise={franchiseForm.franchise}
          onClose={() => setFranchiseForm(null)}
          onDone={(message) => {
            setFranchiseForm(null);
            setNotice(message);
          }}
        />
      ) : null}
      {statusDialog ? (
        <FranchiseStatusDialog
          franchise={statusDialog.franchise}
          status={statusDialog.status}
          onClose={() => setStatusDialog(null)}
          onDone={(message) => {
            setStatusDialog(null);
            setNotice(message);
          }}
        />
      ) : null}
    </div>
  );
}

export function SystemStoresPage() {
  const pagination = useCursorPagination();
  const [notice, setNotice] = useState<string | null>(null);
  const [filters, setFilters] = useState<StoreFilters>({});
  const [storeForm, setStoreForm] = useState<{ mode: 'create' | 'edit'; store?: StoreRow } | null>(null);
  const franchisesQuery = useActiveFranchiseOptions();
  const query = useStorePage(filters, pagination.cursor);
  const activeFranchises = franchisesQuery.data?.rows ?? [];

  const columns = useMemo<DataColumn<StoreRow>[]>(
    () => [
      { header: '매장', render: (row) => row.name },
      { header: '프랜차이즈', render: (row) => row.franchise?.name ?? '-' },
      { header: 'OC', align: 'right', render: (row) => formatOc(row.oc_balance) },
      { header: '포인트 환율', align: 'right', render: (row) => `${formatCurrency(row.krw_per_1000_points)} / 1,000P` },
      { header: '생성일', render: (row) => formatDateTime(row.created_at) },
      {
        header: '작업',
        render: (row) => (
          <button className="secondary-button compact-button" type="button" onClick={() => setStoreForm({ mode: 'edit', store: row })}>
            <Pencil size={15} />
            수정
          </button>
        ),
      },
    ],
    []
  );

  return (
    <div className="page-stack">
      <PageHeading eyebrow="System Admin" title="매장" description="전체 매장을 프랜차이즈 단위로 확인하고 기본 운영 값을 관리합니다." />
      {notice ? <ActionNotice>{notice}</ActionNotice> : null}
      <section className="action-toolbar">
        <button className="primary-button toolbar-button" type="button" onClick={() => setStoreForm({ mode: 'create' })}>
          <Store size={17} />
          매장 추가
        </button>
        <span>{franchisesQuery.isFetching ? '프랜차이즈 목록 갱신 중' : `활성 프랜차이즈 ${activeFranchises.length}개`}</span>
      </section>
      <section className="filter-bar">
        <FilterInput
          label="검색"
          placeholder="매장명"
          value={filters.search ?? ''}
          onChange={(search) => {
            pagination.reset();
            setFilters((current) => ({ ...current, search }));
          }}
        />
        <ToggleField
          checked={!!filters.lowOcOnly}
          label="낮은 매장 OC만"
          onChange={(lowOcOnly) => {
            pagination.reset();
            setFilters((current) => ({ ...current, lowOcOnly }));
          }}
        />
      </section>
      <QuerySection
        columns={columns}
        emptyLabel="조건에 맞는 매장이 없습니다."
        errorLabel="매장 목록을 불러오지 못했습니다."
        hasMore={query.data?.hasMore}
        isFetching={query.isFetching}
        isLoading={query.isLoading}
        onNext={() => pagination.goNext(query.data?.nextCursor ?? null)}
        onRefresh={() => void query.refetch()}
        result={query.data}
      />
      {storeForm ? (
        <StoreFormDialog
          franchises={activeFranchises}
          mode={storeForm.mode}
          store={storeForm.store}
          onClose={() => setStoreForm(null)}
          onDone={(message) => {
            setStoreForm(null);
            setNotice(message);
          }}
        />
      ) : null}
    </div>
  );
}

export function SystemUsersPage() {
  const { profile } = useAuth();
  const pagination = useCursorPagination();
  const [notice, setNotice] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filters, setFilters] = useState<UserFilters>({});
  const [passwordResetDialog, setPasswordResetDialog] = useState<{ user: UserRow } | null>(null);
  const [userDialog, setUserDialog] = useState<{ mode: 'suspend' | 'unsuspend'; user: UserRow } | null>(null);
  const franchisesQuery = useActiveFranchiseOptions();
  const storeOptionsQuery = useStoreOptions();
  const query = useUserPage(filters, pagination.cursor);
  const currentAdminId = profile?.id ?? '';
  const currentAdminRole = profile?.role ?? null;
  const activeFranchises = franchisesQuery.data?.rows ?? [];
  const stores = storeOptionsQuery.data?.rows ?? [];

  const columns = useMemo<DataColumn<UserRow>[]>(
    () => [
      { header: '이름', render: (row) => row.nickname ?? '-' },
      { header: '계정 ID', render: (row) => <code>{getUserAccountId(row)}</code> },
      { header: '역할', render: (row) => <StatusBadge>{row.role}</StatusBadge> },
      { header: '상태', render: (row) => <StatusBadge tone={row.status === 'active' ? 'success' : 'danger'}>{row.status}</StatusBadge> },
      { header: '연락처', render: (row) => maskPhone(row.phone_number ?? row.staff_phone_number) },
      { header: '프랜차이즈', render: (row) => row.franchise?.name ?? '-' },
      { header: '매장', render: (row) => row.store?.name ?? '-' },
      { header: '최근 활동', render: (row) => formatDateTime(row.last_active_at) },
      {
        header: '작업',
        render: (row) => {
          const isCurrentUser = row.id === currentAdminId;
          const canResetPassword = canRequestUserPasswordReset(currentAdminId, currentAdminRole, row);

          return (
            <div className="row-actions">
              {canResetPassword ? (
                <button className="secondary-button compact-button" type="button" onClick={() => setPasswordResetDialog({ user: row })}>
                  <KeyRound size={15} />
                  재설정
                </button>
              ) : null}
              {row.status === 'suspended' ? (
                <button className="secondary-button compact-button" type="button" disabled={isCurrentUser} onClick={() => setUserDialog({ mode: 'unsuspend', user: row })}>
                  <CheckCircle2 size={15} />
                  해제
                </button>
              ) : (
                <button className="danger-outline-button compact-button" type="button" disabled={isCurrentUser} onClick={() => setUserDialog({ mode: 'suspend', user: row })}>
                  <Ban size={15} />
                  정지
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [currentAdminId, currentAdminRole]
  );

  return (
    <div className="page-stack">
      <PageHeading eyebrow="System Admin" title="사용자" description="고객과 관리자 계정의 역할, 상태, 소속을 확인하고 하위 관리자 계정을 생성합니다." />
      {notice ? <ActionNotice>{notice}</ActionNotice> : null}
      <section className="action-toolbar">
        <button className="primary-button toolbar-button" type="button" onClick={() => setCreateDialogOpen(true)}>
          <PlusCircle size={17} />
          관리자 추가
        </button>
        <span>
          {franchisesQuery.isFetching || storeOptionsQuery.isFetching
            ? '소속 목록 갱신 중'
            : `활성 프랜차이즈 ${activeFranchises.length}개 / 매장 ${stores.length}개`}
        </span>
      </section>
      <section className="filter-bar">
        <FilterInput
          label="검색"
          placeholder="이름, ID, 전화번호"
          value={filters.search ?? ''}
          onChange={(search) => {
            pagination.reset();
            setFilters((current) => ({ ...current, search }));
          }}
        />
        <FilterSelect
          label="역할"
          value={filters.role ?? ''}
          options={[
            { label: '전체', value: '' },
            { label: 'customer', value: 'customer' },
            { label: 'staff_admin', value: 'staff_admin' },
            { label: 'super_admin', value: 'super_admin' },
            { label: 'platform_admin', value: 'platform_admin' },
          ]}
          onChange={(role) => {
            pagination.reset();
            setFilters((current) => ({ ...current, role: role || undefined }));
          }}
        />
        <FilterSelect
          label="상태"
          value={filters.status ?? ''}
          options={[
            { label: '전체', value: '' },
            { label: 'active', value: 'active' },
            { label: 'suspended', value: 'suspended' },
          ]}
          onChange={(status) => {
            pagination.reset();
            setFilters((current) => ({ ...current, status: status || undefined }));
          }}
        />
      </section>
      <QuerySection
        columns={columns}
        emptyLabel="조건에 맞는 사용자가 없습니다."
        errorLabel="사용자 목록을 불러오지 못했습니다."
        hasMore={query.data?.hasMore}
        isFetching={query.isFetching}
        isLoading={query.isLoading}
        onNext={() => pagination.goNext(query.data?.nextCursor ?? null)}
        onRefresh={() => void query.refetch()}
        result={query.data}
      />
      {userDialog && currentAdminId ? (
        <UserStatusDialog
          currentAdminId={currentAdminId}
          mode={userDialog.mode}
          user={userDialog.user}
          onClose={() => setUserDialog(null)}
          onDone={(message) => {
            setUserDialog(null);
            setNotice(message);
          }}
        />
      ) : null}
      {passwordResetDialog && currentAdminId ? (
        <UserPasswordResetDialog
          currentAdminId={currentAdminId}
          user={passwordResetDialog.user}
          onClose={() => setPasswordResetDialog(null)}
          onDone={(message) => {
            setPasswordResetDialog(null);
            setNotice(message);
          }}
        />
      ) : null}
      {createDialogOpen ? (
        <CreateAdminUserDialog
          franchises={activeFranchises}
          stores={stores}
          onClose={() => setCreateDialogOpen(false)}
          onDone={(message) => {
            setCreateDialogOpen(false);
            setNotice(message);
          }}
        />
      ) : null}
    </div>
  );
}

export function SystemOcPage() {
  const pagination = useCursorPagination();
  const [notice, setNotice] = useState<string | null>(null);
  const [ocDialog, setOcDialog] = useState<'issue' | 'adjust' | null>(null);
  const [filters, setFilters] = useState<OcFilters>({});
  const requestPagination = useCursorPagination();
  const [requestStatus, setRequestStatus] = useState<OcRequestStatusFilter>('pending');
  const [reviewTarget, setReviewTarget] = useState<{ mode: 'approve' | 'reject'; request: OcRequestRow } | null>(null);
  const franchisesQuery = useActiveFranchiseOptions();
  const query = useOcTransactionPage(filters, pagination.cursor);
  const requestQuery = useOcRequestPage(requestStatus, requestPagination.cursor);
  const activeFranchises = franchisesQuery.data?.rows ?? [];

  const requestColumns = useMemo<DataColumn<OcRequestRow>[]>(
    () => [
      { header: '요청 일시', render: (row) => formatDateTime(row.created_at) },
      {
        header: '대상',
        render: (row) => {
          const target = describeOcRequestTarget(row);
          return (
            <>
              <StatusBadge tone={target.kind === '매장' ? 'neutral' : 'success'}>{target.kind}</StatusBadge> {target.label}
            </>
          );
        },
      },
      { header: '유형', render: (row) => OC_REQUEST_TYPE_LABEL[row.request_type] ?? row.request_type },
      { header: 'OC', align: 'right', render: (row) => formatOc(row.amount) },
      { header: '결제 금액', align: 'right', render: (row) => (row.amount_krw == null ? '-' : formatCurrency(row.amount_krw)) },
      { header: '요청자', render: (row) => row.requested_by_name ?? shortId(row.requested_by) },
      { header: '사유', render: (row) => row.reason },
      {
        header: '상태',
        render: (row) => (
          <>
            <StatusBadge tone={OC_REQUEST_STATUS_TONE[row.status] ?? 'neutral'}>{OC_REQUEST_STATUS_LABEL[row.status] ?? row.status}</StatusBadge>
            {row.review_reason ? <div className="muted-text">{row.review_reason}</div> : null}
          </>
        ),
      },
      {
        header: '작업',
        render: (row) =>
          row.status === 'pending' ? (
            <div className="row-actions">
              <button className="primary-button compact-button" type="button" onClick={() => setReviewTarget({ mode: 'approve', request: row })}>
                <CheckCircle2 size={14} />
                승인
              </button>
              <button className="danger-outline-button compact-button" type="button" onClick={() => setReviewTarget({ mode: 'reject', request: row })}>
                <Ban size={14} />
                반려
              </button>
            </div>
          ) : (
            '-'
          ),
      },
    ],
    [],
  );

  const columns = useMemo<DataColumn<OcTransactionRow>[]>(
    () => [
      { header: '일시', render: (row) => formatDateTime(row.created_at) },
      { header: '유형', render: (row) => <StatusBadge tone={row.amount < 0 ? 'warning' : 'success'}>{ocTransactionTypeLabel(row.transaction_type)}</StatusBadge> },
      { header: 'OC', align: 'right', render: (row) => formatOc(row.amount_oc ?? row.amount) },
      { header: 'KRW', align: 'right', render: (row) => formatCurrency(row.amount_krw) },
      { header: '잔액', align: 'right', render: (row) => formatOc(row.balance_after) },
      { header: '메모', render: (row) => row.description ?? '-' },
    ],
    []
  );

  return (
    <div className="page-stack">
      <PageHeading eyebrow="System Admin" title="OC" description="프랜차이즈와 매장 단위 OC 거래 이력을 확인하고 platform admin 전용 발행/조정을 수행합니다." />
      {notice ? <ActionNotice>{notice}</ActionNotice> : null}
      <section className="action-toolbar">
        <button className="primary-button toolbar-button" type="button" onClick={() => setOcDialog('issue')}>
          <PlusCircle size={17} />
          OC 발행
        </button>
        <button className="secondary-button toolbar-button" type="button" onClick={() => setOcDialog('adjust')}>
          <SlidersHorizontal size={17} />
          OC 조정
        </button>
        <span>{franchisesQuery.isFetching ? '프랜차이즈 목록 갱신 중' : `활성 프랜차이즈 ${activeFranchises.length}개`}</span>
      </section>
      <section className="data-panel">
        <div className="panel-heading">
          <span>
            <Coins size={18} />
            충전 요청
          </span>
          <small>프랜차이즈 관리자가 올린 OC 충전·조정 요청을 심사합니다</small>
        </div>
        <div className="filter-bar" style={{ border: 0, boxShadow: 'none', borderBottom: '1px solid var(--border)', borderRadius: 0 }}>
          <FilterSelect
            label="요청 상태"
            value={requestStatus}
            options={[
              { label: '대기', value: 'pending' },
              { label: '승인', value: 'approved' },
              { label: '반려', value: 'rejected' },
              { label: '취소', value: 'cancelled' },
              { label: '전체', value: '' },
            ]}
            onChange={(value) => {
              requestPagination.reset();
              setRequestStatus(value as OcRequestStatusFilter);
            }}
          />
        </div>
        {requestQuery.isLoading ? (
          <div className="loading-block">충전 요청을 불러오는 중입니다.</div>
        ) : !requestQuery.data ? (
          <div className="error-block">
            <strong>충전 요청을 불러오지 못했습니다.</strong>
            <button className="secondary-button" type="button" onClick={() => void requestQuery.refetch()}>
              <RefreshCcw size={16} />
              다시 시도
            </button>
          </div>
        ) : (
          <>
            <DataTable
              columns={requestColumns}
              emptyLabel={requestStatus === 'pending' ? '대기 중인 충전 요청이 없습니다.' : '조건에 맞는 충전 요청이 없습니다.'}
              rows={requestQuery.data.rows}
            />
            <div className="table-footer">
              <span>{requestQuery.isFetching ? '새로고침 중' : `${requestQuery.data.rows.length}개 표시 중`}</span>
              {requestQuery.data.hasMore ? (
                <button className="secondary-button" type="button" onClick={() => requestPagination.goNext(requestQuery.data?.nextCursor ?? null)}>
                  다음 50개
                </button>
              ) : null}
            </div>
          </>
        )}
      </section>
      <section className="filter-bar">
        <FilterSelect
          label="거래 유형"
          value={filters.transactionType ?? ''}
          options={[{ label: '전체', value: '' }, ...OC_TRANSACTION_TYPE_OPTIONS]}
          onChange={(transactionType) => {
            pagination.reset();
            setFilters({ transactionType: transactionType || undefined });
          }}
        />
      </section>
      <QuerySection
        columns={columns}
        emptyLabel="조건에 맞는 OC 거래가 없습니다."
        errorLabel="OC 거래 목록을 불러오지 못했습니다."
        hasMore={query.data?.hasMore}
        isFetching={query.isFetching}
        isLoading={query.isLoading}
        onNext={() => pagination.goNext(query.data?.nextCursor ?? null)}
        onRefresh={() => void query.refetch()}
        result={query.data}
      />
      {reviewTarget ? (
        <OcRequestReviewDialog
          key={reviewTarget.request.id}
          mode={reviewTarget.mode}
          request={reviewTarget.request}
          onClose={() => setReviewTarget(null)}
          onDone={(message) => {
            setReviewTarget(null);
            setNotice(message);
          }}
        />
      ) : null}
      {ocDialog ? (
        <OcActionDialog
          franchises={activeFranchises}
          mode={ocDialog}
          onClose={() => setOcDialog(null)}
          onDone={(message) => {
            setOcDialog(null);
            setNotice(message);
          }}
        />
      ) : null}
    </div>
  );
}

export function SystemAuditPage() {
  const pagination = useCursorPagination();
  const [filters, setFilters] = useState<AuditFilters>({});
  const query = useAuditLogPage(filters, pagination.cursor);

  const columns = useMemo<DataColumn<AuditLogRow>[]>(
    () => [
      { header: '일시', render: (row) => formatDateTime(row.created_at) },
      { header: '작업', render: (row) => row.action_type },
      { header: '대상', render: (row) => row.target_type },
      { header: '대상 ID', render: (row) => shortId(row.target_id) },
      { header: '관리자', render: (row) => shortId(row.admin_id) },
      { header: '매장', render: (row) => shortId(row.store_id) },
    ],
    []
  );

  return (
    <div className="page-stack">
      <PageHeading eyebrow="System Admin" title="감사 로그" description="고위험 작업과 관리자 변경 이력을 최신순으로 검토합니다." />
      <section className="filter-bar">
        <FilterInput
          label="검색"
          placeholder="작업, 대상, UUID"
          value={filters.search ?? ''}
          onChange={(search) => {
            pagination.reset();
            setFilters((current) => ({ ...current, search }));
          }}
        />
        <FilterInput
          label="대상"
          placeholder="franchise, user, store"
          value={filters.targetType ?? ''}
          onChange={(targetType) => {
            pagination.reset();
            setFilters((current) => ({ ...current, targetType }));
          }}
        />
      </section>
      <QuerySection
        columns={columns}
        emptyLabel="조건에 맞는 감사 로그가 없습니다."
        errorLabel="감사 로그를 불러오지 못했습니다."
        hasMore={query.data?.hasMore}
        isFetching={query.isFetching}
        isLoading={query.isLoading}
        onNext={() => pagination.goNext(query.data?.nextCursor ?? null)}
        onRefresh={() => void query.refetch()}
        result={query.data}
      />
    </div>
  );
}

export function SystemSettingsPage() {
  const { profile } = useAuth();
  const [notice, setNotice] = useState<string | null>(null);
  const [filters, setFilters] = useState<SettingFilters>({});
  const [settingDialog, setSettingDialog] = useState<SystemSettingRow | null>(null);
  const query = useSettingPage(filters);
  const currentAdminId = profile?.id ?? '';

  const columns = useMemo<DataColumn<SystemSettingRow>[]>(
    () => [
      { header: '키', render: (row) => <code>{row.key}</code> },
      { header: '카테고리', render: (row) => row.category ?? '-' },
      { header: '타입', render: (row) => <StatusBadge>{row.value_type}</StatusBadge> },
      { header: '값', render: (row) => <code>{JSON.stringify(row.value)}</code> },
      { header: '설명', render: (row) => row.description ?? '-' },
      { header: '수정일', render: (row) => formatDateTime(row.updated_at) },
      {
        header: '작업',
        render: (row) => (
          <button className="secondary-button compact-button" type="button" onClick={() => setSettingDialog(row)}>
            수정
          </button>
        ),
      },
    ],
    []
  );

  return (
    <div className="page-stack">
      <PageHeading eyebrow="System Admin" title="설정" description="시스템 설정을 확인하고 확인문구 기반으로 안전하게 업데이트합니다." />
      {notice ? <ActionNotice>{notice}</ActionNotice> : null}
      <section className="filter-bar">
        <FilterInput
          label="카테고리"
          placeholder="auth, oc, ranking"
          value={filters.category ?? ''}
          onChange={(category) => setFilters({ category })}
        />
      </section>
      <QuerySection
        columns={columns}
        emptyLabel="조건에 맞는 설정이 없습니다."
        errorLabel="시스템 설정을 불러오지 못했습니다."
        isFetching={query.isFetching}
        isLoading={query.isLoading}
        onRefresh={() => void query.refetch()}
        result={query.data}
      />
      {settingDialog && currentAdminId ? (
        <SettingUpdateDialog
          adminId={currentAdminId}
          setting={settingDialog}
          onClose={() => setSettingDialog(null)}
          onDone={(message) => {
            setSettingDialog(null);
            setNotice(message);
          }}
        />
      ) : null}
    </div>
  );
}

export function SystemAnalyticsPage() {
  return (
    <div className="page-stack">
      <PageHeading eyebrow="System Admin" title="분석" description="플랫폼 분석 화면은 dashboard read model 검증 후 집계 RPC 기준으로 이전합니다." />
      <section className="data-panel">
        <div className="empty-state">
          <Activity size={22} />
          분석 집계는 unbounded client aggregation 없이 Worker 집계 API 계약을 확정한 뒤 연결합니다.
        </div>
      </section>
    </div>
  );
}
